import { z } from 'zod';
import { cnpjValido } from './empresa.js';
import { cuid, dinheiro, isoDate, paginacao, taxa } from './primitives.js';
import { dadosParceiroAtualizarShape, dadosParceiroShape, umPrincipal, type DadosParceiroResponse } from './parceiros.js';

/**
 * Sessao 7: cargos, pessoas (colaboradores), imobilizados, criterios de rateio e a APROPRIACAO por centro de custo.
 * Dinheiro e taxa: string decimal. Percentuais da apropriacao: FRACAO (0.5 = 50%); a UI converte de/para %.
 */

// ---------- Apropriacao por centro de custo (componente unico: Pessoas, Imobilizados, depois CIF) ----------

export const apropriacaoRefereSchema = z.enum(['PESSOA', 'IMOBILIZADO', 'CIF', 'CENTRO_CUSTO']);
export type ApropriacaoRefere = z.infer<typeof apropriacaoRefereSchema>;

/** MANUAL: percentuais digitados (soma = 100%). IGUAL: centros escolhidos, a API divide. CRITERIO: a API calcula pela base do criterio. */
export const apropriacaoEntradaSchema = z.discriminatedUnion('tipo', [
  z.object({
    tipo: z.literal('MANUAL'),
    linhas: z.array(z.object({ centroId: cuid, percentual: taxa })).min(1, 'Informe ao menos um centro').max(200),
  }),
  z.object({ tipo: z.literal('IGUAL'), centroIds: z.array(cuid).min(1, 'Escolha ao menos um centro').max(200) }),
  z.object({ tipo: z.literal('CRITERIO'), criterioId: cuid }),
]);
export type ApropriacaoEntrada = z.infer<typeof apropriacaoEntradaSchema>;

export interface ApropriacaoLinhaResponse {
  centroId: string;
  caminho: string;
  nome: string;
  /** Fracao (string decimal). */
  percentual: string;
}
export interface ApropriacaoResponse {
  id: string;
  referTo: ApropriacaoRefere;
  tipo: 'MANUAL' | 'IGUAL' | 'CRITERIO';
  criterioId: string | null;
  criterioNome: string | null;
  linhas: ApropriacaoLinhaResponse[];
  /** Soma das linhas (fracao). Para CRITERIO e IGUAL e 1 por construcao. */
  soma: string;
  /** Preenchido quando o criterio nao pode ser calculado (ex. base total zero): linhas vem vazias. */
  erro?: { code: string; message: string };
}

/** Previa da apropriacao (nada e gravado): a API calcula os percentuais de IGUAL e CRITERIO para a tela mostrar antes de salvar. */
export const apropriacaoPreviaSchema = z.object({ referTo: apropriacaoRefereSchema, entrada: apropriacaoEntradaSchema });
export type ApropriacaoPreviaDto = z.infer<typeof apropriacaoPreviaSchema>;
export type ApropriacaoPreviaResponse = Omit<ApropriacaoResponse, 'id'>;

// ---------- Cargos ----------

const nomeCargo = z.string().trim().min(1, 'Informe o nome do cargo').max(120, 'O nome tem no máximo 120 caracteres');
export const criarCargoSchema = z.object({ nome: nomeCargo });
export type CriarCargoDto = z.infer<typeof criarCargoSchema>;
export const atualizarCargoSchema = z
  .object({ nome: nomeCargo, ativo: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarCargoDto = z.infer<typeof atualizarCargoSchema>;
export const listarCargosSchema = paginacao.extend({
  q: z.string().trim().min(1).max(100).optional(),
  ativo: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
});
export type ListarCargosQuery = z.infer<typeof listarCargosSchema>;
export interface CargoResponse {
  id: string;
  nome: string;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------- Documentos (digito verificador) ----------

export function cpfValido(cpf: string): boolean {
  if (!/^\d{11}$/.test(cpf) || /^(\d)\1{10}$/.test(cpf)) return false;
  const dig = (n: number) => {
    let soma = 0;
    for (let i = 0; i < n; i += 1) soma += Number(cpf[i]) * (n + 1 - i);
    const r = (soma * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return dig(9) === Number(cpf[9]) && dig(10) === Number(cpf[10]);
}

// ---------- Pessoas ----------

export const naturezaPessoaSchema = z.enum(['FISICA', 'JURIDICA']);
export const turnoPessoaSchema = z.enum(['1', '2', '3', 'COMERCIAL']);
export type TurnoPessoa = z.infer<typeof turnoPessoaSchema>;

const pessoaBase = z.object({
  natureza: naturezaPessoaSchema,
  nome: z.string().trim().min(1, 'Informe o nome').max(200, 'O nome tem no máximo 200 caracteres'),
  identificador: z.string().trim().max(50).optional(),
  /** CPF (FISICA) ou CNPJ (JURIDICA), so digitos. */
  documento: z.string().regex(/^\d{11}$|^\d{14}$/, 'Documento só com dígitos (CPF 11, CNPJ 14)').optional(),
  nascimento: isoDate.optional(),
  /** Sessao 16: departamento (texto livre) e turno. */
  departamento: z.string().trim().min(1).max(100).optional(),
  turno: turnoPessoaSchema.optional(),
  /** Admissao e demissao: a demissao encerra a vigencia dos parametros de trabalho. */
  admissao: isoDate.optional(),
  demissao: isoDate.optional(),
  descricao: z.string().trim().max(500).optional(),
  ...dadosParceiroShape,
});

function documentoCoerente(v: { natureza?: 'FISICA' | 'JURIDICA' | undefined; documento?: string | null | undefined }, ctx: z.RefinementCtx): void {
  if (!v.documento || !v.natureza) return;
  const ok = v.natureza === 'FISICA' ? cpfValido(v.documento) : cnpjValido(v.documento);
  if (!ok) ctx.addIssue({ code: 'custom', path: ['documento'], message: v.natureza === 'FISICA' ? 'CPF inválido' : 'CNPJ inválido' });
}

function datasCoerentes(v: { admissao?: string | null | undefined; demissao?: string | null | undefined }, ctx: z.RefinementCtx): void {
  if (v.admissao && v.demissao && v.demissao < v.admissao) ctx.addIssue({ code: 'custom', path: ['demissao'], message: 'A demissão não pode ser antes da admissão' });
}

export const criarPessoaSchema = pessoaBase.extend({ parceiroId: cuid.optional() }).superRefine(documentoCoerente).superRefine(datasCoerentes).superRefine((v, ctx) => umPrincipal(v.enderecos ?? [], ctx));
export type CriarPessoaDto = z.infer<typeof criarPessoaSchema>;
export const atualizarPessoaSchema = pessoaBase
  .partial()
  .extend({
    ativo: z.boolean().optional(),
    documento: pessoaBase.shape.documento.nullable().optional(),
    nascimento: isoDate.nullable().optional(),
    departamento: pessoaBase.shape.departamento.nullable().optional(),
    turno: turnoPessoaSchema.nullable().optional(),
    admissao: isoDate.nullable().optional(),
    demissao: isoDate.nullable().optional(),
    ...dadosParceiroAtualizarShape,
  })
  .superRefine(documentoCoerente)
  .superRefine(datasCoerentes)
  .superRefine((v, ctx) => umPrincipal(v.enderecos ?? [], ctx))
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarPessoaDto = z.infer<typeof atualizarPessoaSchema>;
export const listarPessoasSchema = paginacao.extend({
  q: z.string().trim().min(1).max(100).optional(),
  ativo: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  /** Sessao 24: so quem tem este centro na apropriacao da vigencia atual. */
  centroId: cuid.optional(),
});
export type ListarPessoasQuery = z.infer<typeof listarPessoasSchema>;

/**
 * Parametros de trabalho com VIGENCIA: nova vigencia fecha a anterior (nunca retroativa). `gratificacoes` ("Gratificacoes e adicionais")
 * entram nos encargos; `bonus` ("Bonus e beneficios") nao. Salario total (base dos encargos) = salario + gratificacoes (a API soma).
 */
export const criarPessoaParametroSchema = z.object({
  validFrom: isoDate,
  cargoId: cuid,
  salario: dinheiro,
  gratificacoes: dinheiro.default('0'),
  bonus: dinheiro.default('0'),
  horasDia: dinheiro,
  diasMes: dinheiro,
  terceirizado: z.boolean().default(false),
  fornecedorId: cuid.optional(),
  apropriacao: apropriacaoEntradaSchema.optional(),
  motivo: z.string().trim().max(300).optional(),
});
export type CriarPessoaParametroDto = z.infer<typeof criarPessoaParametroSchema>;

/**
 * Corrigir uma vigencia ja gravada (ADMIN_EMPRESA, ADMIN_TENANT, CONSULTOR): motivo obrigatorio e auditado. Mudar o inicio acerta o fim da anterior.
 * Vigencia usada por rateio fechado nao pode ser alterada nem excluida (`travada`).
 */
export const alterarPessoaParametroSchema = z
  .object({
    validFrom: isoDate.optional(),
    cargoId: cuid.optional(),
    salario: dinheiro.optional(),
    gratificacoes: dinheiro.optional(),
    bonus: dinheiro.optional(),
    horasDia: dinheiro.optional(),
    diasMes: dinheiro.optional(),
    terceirizado: z.boolean().optional(),
    fornecedorId: cuid.nullable().optional(),
    apropriacao: apropriacaoEntradaSchema.optional(),
    motivo: z.string().trim().min(3, 'Informe o motivo').max(300, 'O motivo tem no máximo 300 caracteres'),
  })
  .refine((v) => Object.keys(v).some((k) => k !== 'motivo' && v[k as keyof typeof v] !== undefined), 'Informe ao menos um campo a alterar');
export type AlterarPessoaParametroDto = z.infer<typeof alterarPessoaParametroSchema>;
export const excluirPessoaParametroSchema = z.object({ motivo: z.string().trim().min(3, 'Informe o motivo').max(300, 'O motivo tem no máximo 300 caracteres') });
export type ExcluirPessoaParametroDto = z.infer<typeof excluirPessoaParametroSchema>;
export const paramVigenciaPessoa = z.object({ id: cuid, parametroId: cuid });

export interface PessoaParametroResponse {
  id: string;
  validFrom: string;
  validTo: string | null;
  cargo: { id: string; nome: string };
  salario: string;
  /** Gratificacoes e adicionais (entram nos encargos). */
  gratificacoes: string;
  /** Bonus e beneficios (sem encargos). */
  bonus: string;
  /** Base dos encargos: salario + gratificacoes. */
  salarioTotal: string;
  horasDia: string;
  diasMes: string;
  terceirizado: boolean;
  fornecedor: { id: string; nome: string } | null;
  apropriacao: ApropriacaoResponse | null;
  /** salarioTotal x (1 + encargos da empresa) + bonus; terceirizado = salarioTotal + bonus. Apenas exibicao. */
  custoMensal: string | null;
  encargosSociais: string | null;
  motivo: string | null;
  /** Quem gravou a vigencia e quando (ISO). */
  criadoPor: { id: string; nome: string } | null;
  criadoEm: string;
  /** Usada por rateio fechado: nao pode ser editada nem excluida (hoje sempre false: o rateio ainda nao existe). */
  travada: boolean;
}
export interface PessoaResponse extends DadosParceiroResponse {
  id: string;
  natureza: 'FISICA' | 'JURIDICA';
  nome: string;
  identificador: string | null;
  documento: string | null;
  nascimento: string | null;
  departamento: string | null;
  turno: TurnoPessoa | null;
  admissao: string | null;
  demissao: string | null;
  descricao: string | null;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
  /** Vigente hoje (ou a ultima, se nenhuma cobre hoje). */
  parametroVigente: PessoaParametroResponse | null;
  /** Historico completo (so no GET :id). */
  parametros?: PessoaParametroResponse[];
}

// ---------- Imobilizados ----------

export const imobilizadoTipoSchema = z.enum(['MAQUINAS_EQUIPAMENTOS', 'IMOVEL', 'VEICULO']);
export type ImobilizadoTipo = z.infer<typeof imobilizadoTipoSchema>;

const quantidadeImobilizado = dinheiro.refine((v) => Number(v) > 0, 'A quantidade deve ser maior que zero');
const idadeImobilizado = z.number().int().min(0).max(1200);
/** Campos SEM default: o PATCH parcial nao pode reaplicar default (zerava idade e quantidade quando omitidos). */
const imobilizadoCampos = z.object({
  tipo: imobilizadoTipoSchema,
  identificador: z.string().trim().max(50).optional(),
  descricao: z.string().trim().min(1, 'Informe a descrição').max(200),
  valorUnitario: dinheiro,
  quantidade: quantidadeImobilizado,
  vidaUtilMeses: z.number().int().min(0).max(1200),
  idadeMeses: idadeImobilizado,
  /** Quando informada, a idade na competencia sai dela (o campo manual segue para quem nao tem a data). */
  dataAquisicao: isoDate.optional(),
  depreciavel: z.boolean(),
  /** Sessao 43: fim da depreciacao (opcional); manda sobre a data de aquisicao + vida util e sobre a idade. Deprecia ate o mes dessa data, inclusive. */
  depreciarAte: isoDate.optional(),
  observacao: z.string().trim().max(500).optional(),
  modelo: z.string().trim().max(100).optional(),
  marca: z.string().trim().max(100).optional(),
  /** Consumo em W/h e uso em h/dia (bases dos criterios Watts e Valor hora/maquina). */
  consumoWh: dinheiro.optional(),
  usoHorasDia: dinheiro.refine((v) => Number(v) <= 24, 'No máximo 24 h/dia').optional(),
  apropriacao: apropriacaoEntradaSchema.optional(),
});
const imobilizadoBase = imobilizadoCampos.extend({
  quantidade: quantidadeImobilizado.default('1'),
  /** Sessao 44: sem o campo a idade fica "nao informada" (0); informar 0 conta como idade informada. */
  idadeMeses: idadeImobilizado.optional(),
  depreciavel: z.boolean().default(true),
});
export const criarImobilizadoSchema = imobilizadoBase;
export type CriarImobilizadoDto = z.infer<typeof criarImobilizadoSchema>;
export const atualizarImobilizadoSchema = imobilizadoCampos
  .partial()
  .extend({ ativo: z.boolean().optional(), apropriacao: apropriacaoEntradaSchema.nullable().optional(), dataAquisicao: isoDate.nullable().optional(), depreciarAte: isoDate.nullable().optional(), observacao: z.string().trim().max(500).nullable().optional() })
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarImobilizadoDto = z.infer<typeof atualizarImobilizadoSchema>;
export const listarImobilizadosSchema = paginacao.extend({
  q: z.string().trim().min(1).max(100).optional(),
  tipo: imobilizadoTipoSchema.optional(),
  ativo: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  /** Sessao 43: so os que depreciam sem fim (sem data limite, sem data de aquisicao e sem idade informada). */
  semLimite: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
});
export type ListarImobilizadosQuery = z.infer<typeof listarImobilizadosSchema>;
export interface ImobilizadoResponse {
  id: string;
  tipo: ImobilizadoTipo;
  identificador: string | null;
  descricao: string;
  quantidade: string;
  vidaUtilMeses: number;
  /** Com `dataAquisicao`, a idade de HOJE calculada por ela; sem, o valor manual. */
  idadeMeses: number;
  /** Sessao 44: a idade foi informada (inclusive 0). */
  idadeInformada: boolean;
  /** Sessao 43: depreciavel sem data limite, sem data de aquisicao e sem idade informada: deprecia sem fim (aviso na tela). */
  semLimiteDepreciacao: boolean;
  /** Sessao 42: veio da importacao do XARP antigo (a idade la era estatica: nunca avancava). */
  importadoXarp: boolean;
  dataAquisicao: string | null;
  depreciavel: boolean;
  depreciarAte: string | null;
  observacao: string | null;
  modelo: string | null;
  marca: string | null;
  consumoWh: string | null;
  usoHorasDia: string | null;
  ativo: boolean;
  apropriacao: ApropriacaoResponse | null;
  createdAt: string;
  updatedAt: string;
  /** So com custo.ver (a chave nao existe sem a permissao): valor e depreciacao pelo motor. */
  valorUnitario?: string;
  depreciacaoMensal?: string;
  depreciacaoTotal?: string;
  /** Sessao 43: depreciacao mensal que o bem teria se fosse depreciavel (so quando `depreciavel` e falso e ha vida util; custo.ver). */
  depreciacaoSeDepreciavel?: string;
}

// ---------- Criterios de rateio ----------

export const criterioBaseSchema = z.enum(['M2', 'FUNCIONARIOS', 'SALARIO', 'WATTS', 'VALOR_HORA_MAQUINA', 'MAQUINAS']);
export type CriterioBase = z.infer<typeof criterioBaseSchema>;

const criterioBaseCampos = z.object({
  identificador: z.string().trim().min(1, 'Informe o identificador').max(50),
  descricao: z.string().trim().min(1, 'Informe a descrição').max(200),
  base: criterioBaseSchema,
  todosCentros: z.boolean().default(true),
  /** Centros considerados (ordenados). Ignorado quando todosCentros. */
  centroIds: z.array(cuid).max(500).default([]),
});
export const criarCriterioSchema = criterioBaseCampos.refine((v) => v.todosCentros || v.centroIds.length > 0, {
  path: ['centroIds'],
  message: 'Escolha ao menos um centro ou marque "considerar todos os centros"',
});
export type CriarCriterioDto = z.infer<typeof criarCriterioSchema>;
export const atualizarCriterioSchema = criterioBaseCampos
  .partial()
  .extend({ ativo: z.boolean().optional() })
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarCriterioDto = z.infer<typeof atualizarCriterioSchema>;
export interface CriterioResponse {
  id: string;
  identificador: string;
  descricao: string;
  base: CriterioBase;
  todosCentros: boolean;
  centros: { centroId: string; caminho: string; nome: string; ordem: number }[];
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}
export const exemploCriterioSchema = z.object({ valor: dinheiro });
export type ExemploCriterioQuery = z.infer<typeof exemploCriterioSchema>;
/** Painel Exemplo: valor total digitado -> R$ e % por centro (motor percentuaisDoCriterio). */
export interface ExemploCriterioResponse {
  valor: string;
  linhas: { centroId: string; caminho: string; nome: string; base: string; percentual: string; valor: string }[];
  somaPercentual: string;
  somaValor: string;
  erro?: { code: string; message: string };
}

// ---------- Minutos disponiveis do centro (calculados) ----------

export interface MinutosCentroResponse {
  centroId: string;
  tipoTrabalho: 'HOMEM' | 'MAQUINA' | 'MISTO' | null;
  /** 'calculado' = pessoas/imobilizados alocados; 'manual' = nenhum alocado, vale o valor cadastrado. */
  origem: 'calculado' | 'manual';
  minutosPessoas: string;
  minutosMaquinas: string;
  total: string;
  /** Valor cadastrado a mao (para mostrar o aviso 'manual'). */
  manual: string | null;
  pessoasAlocadas: number;
  imobilizadosAlocados: number;
}

/** Sessao 43: relatorio "Imobilizados nao depreciaveis e sem limite" (custo.ver): bens desmarcados e bens que depreciam sem fim, com a depreciacao mensal e o efeito no CPM por centro. */
export interface ImobilizadosSemLimiteRelatorio {
  competencia: string;
  linhas: { id: string; descricao: string; identificador: string | null; tipo: ImobilizadoTipo; situacao: 'DESMARCADO' | 'SEM_LIMITE'; observacao: string | null; centros: string; valorTotal: string; vidaUtilMeses: number; depreciacaoMensal: string }[];
  /** `depreciacaoDesmarcados` = o que entraria se os desmarcados fossem ligados; `depreciacaoSemLimite` = o que os bens sem fim ja depreciam (ja esta no CPM atual). */
  centros: { centroId: string; codigo: string; nome: string; depreciacaoDesmarcados: string; depreciacaoSemLimite: string; cpmAtual: string | null; cpmSeLigados: string | null; efeitoCpm: string | null }[];
  totalDesmarcados: string;
  totalSemLimite: string;
}
